import React, { useEffect, useRef, useState } from 'react';
import { SOURCE_TYPES } from './SourceRow';

// Add a source by hand.
//
// The company picker searches the WHOLE book, monitored or not. It used to offer a
// fixed list of monitored companies only, on the reasoning that a source on an
// unmonitored company would never be scanned — true when it was written, false
// since AddSource began switching monitoring on in the same transaction. On a fresh
// tenant that rule left a dropdown containing one company.
//
// Searching happens server-side because a tenant has 13,601 companies, which is not
// a list to ship to a browser and filter there.

const DEFAULT_URL_PLACEHOLDER = 'https://example.com/careers';

// How long to wait after the last keystroke before asking the server. Long enough
// that typing a company name is one request rather than eight, short enough that
// the list feels like it is keeping up.
const SEARCH_DEBOUNCE_MS = 200;

// signalHint compresses a company's signal to something that fits on one line of a
// dropdown. Deliberately terse — the full rationale appears once the company is
// chosen; this only has to tell two similar rows apart.
function signalHint(c) {
  const bits = [];
  if (c.job_count) bits.push(`${c.job_count} roles`);
  if (c.people_count) bits.push(`${c.people_count} contacts`);
  return bits.join(' · ');
}

export default function AddSourceDialog({ onSearch, onAdd, onClose, error = '', busy = false, prefill = null }) {
  // query is what is IN the box; company is what has been chosen. They are separate
  // because typing after a choice must not silently keep submitting the old company.
  const [query, setQuery] = useState(prefill?.companyName || '');
  const [company, setCompany] = useState(
    prefill ? { company_id: prefill.companyId, name: prefill.companyName, suggested_url: '' } : null
  );
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  // Which option the keyboard is on. -1 = none, so Enter submits nothing by accident.
  const [active, setActive] = useState(-1);
  const [searchError, setSearchError] = useState('');
  const [url, setUrl] = useState('');
  const [sourceType, setSourceType] = useState('careers');
  const [note, setNote] = useState('');

  // Guards against a slow response for an earlier query landing after a newer one
  // and repainting the list with stale matches — the same race the page's own
  // loadPage guards with pageSeq.
  const seq = useRef(0);

  useEffect(() => {
    // A chosen company whose name is still in the box is not a search: re-querying
    // would reopen the list over a decision the user already made.
    if (!onSearch || company?.name === query) return undefined;
    const q = query.trim();
    if (!q) {
      setMatches([]);
      return undefined;
    }
    const mine = ++seq.current;
    const timer = setTimeout(() => {
      Promise.resolve(onSearch(q))
        .then((rows) => {
          if (mine !== seq.current) return; // superseded by a newer keystroke
          setSearchError('');
          setMatches(rows || []);
          setActive(-1);
          setOpen(true);
        })
        .catch((e) => {
          if (mine !== seq.current) return;
          // Never an empty list on failure: that reads as "you have no such
          // company", which is a different and wrong statement.
          setSearchError(e.message);
          setMatches([]);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, onSearch, company]);

  const choose = (c) => {
    setCompany(c);
    setQuery(c.name);
    setOpen(false);
    setActive(-1);
    setMatches([]);
  };

  // A combobox with no keyboard is a mouse trap. Arrow keys move the active option,
  // Enter takes it, Escape closes the list without choosing.
  const onKeyDown = (e) => {
    if (!open || !matches.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? matches.length - 1 : i - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      choose(matches[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // The suggestion is a PLACEHOLDER, never a value. A prefilled value would be
  // submitted by a user who never looked at the field, committing a page they did
  // not choose; a placeholder shows what we hold and still requires them to say so.
  //
  // The API leaves suggested_url empty when the page is already a source — or was
  // dismissed — so a page the user refused is never offered back to them here.
  const urlPlaceholder = company?.suggested_url || DEFAULT_URL_PLACEHOLDER;

  // Taking the suggestion sets the page KIND as well as the URL. Filling only the
  // URL would leave the kind on its 'careers' default, so the company's website
  // would be filed as a careers page — and app.companies.website, which only the
  // website and linkedin kinds write to, would never be filled at all.
  const useSuggestion = () => {
    setUrl(company.suggested_url);
    if (company.suggested_type) setSourceType(company.suggested_type);
  };
  // Offered only while the field is still empty: with something typed, the button
  // either overwrites the user's own work or sits there doing nothing.
  const canUseSuggestion = Boolean(company?.suggested_url) && !url;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-6" role="dialog" aria-modal="true" aria-label="Add a source">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl overflow-hidden">
        <header className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">
            {prefill ? `What should we watch for ${prefill.companyName}?` : 'Add a source'}
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {prefill
              ? 'This company has no website or LinkedIn on record, so we cannot follow what they do next until you add a page.'
              : 'Private to your workspace — no other SignalCue customer sees it.'}
          </p>
        </header>
        <div className="px-5 py-4 space-y-3">
          {error && (
            <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">{error}</p>
          )}
          <div className="relative">
            <label htmlFor="ss-company" className="block text-xs font-semibold text-gray-700 mb-1">Company</label>
            <input
              id="ss-company"
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
              aria-controls="ss-company-list"
              autoComplete="off"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setCompany(null); }}
              onFocus={() => matches.length && setOpen(true)}
              onKeyDown={onKeyDown}
              aria-activedescendant={active >= 0 ? `ss-company-opt-${active}` : undefined}
              placeholder="Search your companies…"
              className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-[#00C6AE]"
            />
            {searchError && <p className="mt-1 text-xs text-red-700">{searchError}</p>}
            {company && !open && (
              // WHY this company is worth watching, in the queue's own words — the
              // sentences come from the same builder, so one company cannot read
              // two different ways across the two surfaces.
              <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                {company.reasons && company.reasons.length > 0 ? (
                  <ul className="space-y-1">
                    {company.reasons.map((r) => (
                      <li key={r.code} className="text-[12.5px] text-gray-700 flex gap-2 items-start">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00C6AE] shrink-0" aria-hidden="true" />
                        <span>{r.text}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  // Stating the absence rather than rendering an empty box: silence
                  // here is information — the company is quiet — while a blank panel
                  // would read as a failed load.
                  <p className="text-[12.5px] text-gray-500">
                    Nothing on your books for this company yet — no roles, contacts or
                    recorded contact. Watching it is a bet on what comes next.
                  </p>
                )}
              </div>
            )}
            {open && matches.length > 0 && (
              <ul
                id="ss-company-list"
                role="listbox"
                className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg"
              >
                {/* The option IS the li: an ARIA listbox option must not wrap a
                    focusable control, and when it did, a click landed on the li
                    while the handler sat on an inner button — so picking a company
                    silently did nothing. onMouseDown prevents the input's blur from
                    closing the list before the click lands. */}
                {matches.map((c, i) => (
                  <li
                    key={c.company_id}
                    id={`ss-company-opt-${i}`}
                    role="option"
                    aria-selected={company?.company_id === c.company_id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(c)}
                    onMouseEnter={() => setActive(i)}
                    className={`px-3 py-2 text-sm cursor-pointer flex items-baseline gap-2 ${
                      i === active ? 'bg-teal-50' : 'hover:bg-teal-50'
                    }`}
                  >
                    <span className="flex-1 truncate">{c.name}</span>
                    {/* A hint of the signal, because real data has duplicate names:
                        two "Accenture Solutions" are indistinguishable otherwise,
                        and picking the wrong one attaches the source to the wrong
                        company. */}
                    <span className="shrink-0 text-[11px] text-gray-400 tabular-nums">
                      {signalHint(c)}
                    </span>
                    {c.signals_on && (
                      <span className="shrink-0 text-[10px] uppercase tracking-wider text-[#00695C]">watching</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label htmlFor="ss-url" className="block text-xs font-semibold text-gray-700 mb-1">URL</label>
            <div className="relative">
              <input id="ss-url" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder={urlPlaceholder}
                className={`w-full font-mono text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 ${
                  canUseSuggestion ? 'pr-24' : ''
                }`} />
              {canUseSuggestion && (
                // Inside the field, at the end of the placeholder it fills — the
                // shortest distance between seeing the suggestion and taking it.
                // Labelled rather than a bare chevron: an arrow beside a URL box
                // reads as "go there", which is the opposite of what it does.
                <button
                  type="button"
                  onClick={useSuggestion}
                  className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-md border border-[#00C6AE] bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-[#00695C] hover:bg-teal-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00C6AE]"
                >
                  Use this page
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="3" aria-hidden="true">
                    <path d="M5 12h13M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </div>
            {company?.suggested_url && (
              <p className="mt-1 text-[11.5px] text-gray-400">
                {url === company.suggested_url
                  ? 'Taken from their record — not watched until you add it.'
                  : 'We already hold this page for them, and nothing is watching it yet.'}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="ss-type" className="block text-xs font-semibold text-gray-700 mb-1">What kind of page is it?</label>
              <select id="ss-type" value={sourceType} onChange={(e) => setSourceType(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5">
                {SOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="ss-note" className="block text-xs font-semibold text-gray-700 mb-1">
                Note <span className="font-normal text-gray-400">optional</span>
              </label>
              <input id="ss-note" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="why you're watching this"
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5" />
            </div>
          </div>
          <p className="text-xs text-gray-400">
            Adding a page starts watching that company — you do not need to switch it on first.
          </p>
        </div>
        <footer className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 text-gray-600">Cancel</button>
          <button type="button" disabled={busy || !url || !company}
            onClick={() => onAdd({ companyId: company.company_id, url, sourceType, note })}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-[#00C6AE] text-white hover:bg-[#00b09b] disabled:opacity-50">
            Add source
          </button>
        </footer>
      </div>
    </div>
  );
}
